import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import PageHero from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import client from "@/lib/api";

const CATEGORIES = ["all", "Automotriz", "Industrial", "Naval", "Suministros"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Blog — Mantenimiento automotriz, industrial y naval",
    description: "Artículos, recomendaciones y novedades sobre mantenimiento automotriz, industrial y naval para prolongar la vida útil de tus equipos.",
    path: "/blog",
  });

  useEffect(() => {
    setLoading(true);
    client.get("/blog", { params: { category } }).then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, [category]);

  return (
    <>
      <PageHero
        overline="Blog"
        title="Noticias y contenido de interés"
        subtitle="Compartimos información y recomendaciones para ayudarte a tomar mejores decisiones y prolongar la vida útil de tus equipos y vehículos."
        image="https://images.unsplash.com/photo-1738918901587-41f3f3f88afd"
        crumbs={[{ label: "Blog" }]}
      />

      <section className="bg-gema-black py-16 md:py-24">
        <div className="container-gema">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="blog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-filter-${c.toLowerCase()}`}
                className={`px-5 py-2 font-mono text-xs uppercase tracking-wider border transition-colors duration-300 ${
                  category === c ? "bg-gema-red border-gema-red text-white" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {c === "all" ? "Todos" : c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-white/40 font-mono text-sm">Cargando artículos…</p>
          ) : posts.length === 0 ? (
            <p className="text-white/40 font-mono text-sm" data-testid="blog-empty">No hay artículos en esta categoría.</p>
          ) : (
            <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <RevealItem key={p.id}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-item-${p.slug}`} className="group block h-full border border-white/10 hover:border-gema-red/40 transition-colors duration-300">
                    <div className="relative overflow-hidden h-[220px]">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-gema-red px-3 py-1">{p.category}</span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-white/40 text-xs font-mono uppercase tracking-wider">
                        <Clock className="h-3 w-3" /> {p.read_time}
                      </div>
                      <h3 className="font-heading font-bold text-lg text-white mt-3 leading-snug group-hover:text-gema-red transition-colors duration-300">{p.title}</h3>
                      <p className="text-white/50 text-sm mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="inline-flex items-center gap-2 mt-4 text-gema-red font-heading text-sm font-bold uppercase tracking-wider">
                        Leer más <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </RevealItem>
              ))}
            </RevealGroup>
          )}
        </div>
      </section>
    </>
  );
}
